import { safeParseJSON } from './../../../../utils/safeParseJson';
import { useCallback, useMemo, useRef } from 'react';
import { Discount } from 'shared/types/cart/Discount';
import useSWR, { mutate } from 'swr';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import { Cart } from 'shared/types/cart';
import { Order } from 'shared/types/cart/Order';
import { Variant } from 'shared/types/product';
import { revalidateOptions } from 'frontastic';
import { CartDetails, UseCartReturn } from './types';
import { ALLOWBACKORDER } from 'helpers/utils/constantStrings';
import { Address } from 'shared/types/account';
import { USECART_END_POINTS } from 'helpers/utils/endPoints';
import { CART_LINEITEM_MAX_QUANTITY } from '../../../../utils/errorCode';
import toast from 'react-hot-toast';
import { useFormat } from 'helpers/hooks/useFormat';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { useLocale } from '../useLocale';

const useCart = (): UseCartReturn => {
  const extensions = sdk.composableCommerce;
  const updateCheckoutSessionRef = useRef<boolean>(false);
  const isUpdateCheckoutSession = updateCheckoutSessionRef.current;
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { currency } = useI18n();
  const { locale } = useLocale();
  const result = useSWR(`${USECART_END_POINTS?.getCart}`, extensions.cart.getCart, revalidateOptions);
  const setIsUpdateCheckoutSession = (isUpdate: boolean) => (updateCheckoutSessionRef.current = isUpdate);
  const handleMaxLineitemQuantityError = (res: SDKResponse<unknown>) => {
    if (res?.isError && res?.error?.message?.includes(CART_LINEITEM_MAX_QUANTITY)) {
      toast.error(
        formatErrorMessage({
          id: 'cart.max.lineitem.qty',
          defaultMessage: 'Maximum lineitem quantity reached for this item.',
        }),
      );
    }
  };

  const shippingMethodsResults = useSWR(
    `${USECART_END_POINTS?.getShippingMethods}`,
    extensions.cart.getShippingMethods,
    revalidateOptions,
  );

  const data = result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };
  const shippingMethods = shippingMethodsResults.data?.isError ? {} : { data: shippingMethodsResults.data?.data };
  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const hasOutOfStockItems = !!data?.data?.lineItems?.some(
    (lineItem) => !lineItem.variant?.isOnStock && lineItem?.variant?.attributes?.allowBackorder !== 'true',
  );

  const transaction = useMemo(() => mapCosts({ reference: 'cart', cart: data.data, currency }), [data.data, currency]);

  const addItem = useCallback(async (variant: any, quantity: number, productUrl: string, isCampaignItems?: boolean) => {
    const extensions = sdk.composableCommerce;
    const allowBackorder = variant?.attributesRaw
      ? variant.attributesRaw.find((attr: { name: string }) => attr.name === ALLOWBACKORDER)?.value[locale] === 'true'
      : variant?.attributes?.allowBackorder === 'true';

    const payload = {
      variant: {
        sku: variant.sku,
        count: quantity,
        isAllowBackorder: allowBackorder ?? false,
        lineItemUrl: productUrl.endsWith('/') ? productUrl.slice(0, -1) : productUrl,
      },
      isCampaignItems,
    };

    const res = await extensions.cart.addItem(payload);
    handleMaxLineitemQuantityError(res);
    mutate(`${USECART_END_POINTS?.getCart}`, res);
    return res.isError ? {} : res.data;
  }, []);

  const addBonusItem = useCallback(
    async (
      parentSku: string,
      variant?: Variant,
      campaignItemSkus?: string[],
      campaignItemsUrl?: { sku: string; url: string }[],
    ) => {
      const payload = {
        parentSku,
        variant: variant?.sku
          ? {
              sku: variant.sku,
              count: 1,
            }
          : undefined,
        campaignItemSkus,
        campaignItemsUrl,
      };
      const res = await sdk.callAction({
        actionName: `${USECART_END_POINTS?.addBonusProductToCart}`,
        payload,
      });
      handleMaxLineitemQuantityError(res);
      if (!res.isError) {
        mutate(`${USECART_END_POINTS?.getCart}`, res);
      }
      return res.isError ? false : (res.data as Cart) ? true : false;
    },
    [],
  );

  const orderCart = useCallback(async (paymentIds?: string[]) => {
    let res: any = await sdk.callAction({ actionName: `${USECART_END_POINTS?.checkout}`, payload: { paymentIds } });
    mutate(`${USECART_END_POINTS?.getCart}`, res);
    mutate(`${USECART_END_POINTS?.getAccount}`);
    if (!res?.data?.orderId) {
      res = await sdk.callAction({ actionName: `${USECART_END_POINTS?.checkout}`, payload: { paymentIds } });
    }
    return (res.isError ? {} : res.data) as Order;
  }, []);

  const validateCart = useCallback(
    async ({ paymentId, unzerPaymentId, cartId }: { paymentId: string; unzerPaymentId?: string; cartId: string }) => {
      const res = await sdk.callAction({
        actionName: `${USECART_END_POINTS?.verifyPayment}`,
        payload: { ctPaymentId: paymentId, paymentId: unzerPaymentId, cartId },
      });
      return (res.isError ? {} : res.data) as Order;
    },
    [],
  );

  const getOrder = useCallback(async (orderId: Order['orderId']) => {
    const res = await sdk.callAction({ actionName: `${USECART_END_POINTS?.getOrder}`, payload: { orderId: orderId } });
    mutate(`${USECART_END_POINTS?.getCart}`, res);

    return (res.isError ? res : res.data) as Order;
  }, []);

  const orderHistory = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getOrderHistory();

    return res.isError ? ([] as Order[]) : (res.data as Order[]);
  }, []);

  const getProjectSettings = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.project.getSettings();

    return res.isError ? {} : res.data;
  }, []);

  const removeItem = useCallback(async (lineItemId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: { id: lineItemId },
    };

    const res = await extensions.cart.removeItem(payload);
    mutate(`${USECART_END_POINTS?.getCart}`, res);
  }, []);

  const updateItem = useCallback(async (lineItemId: string, newQuantity: number) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: {
        id: lineItemId,
        count: newQuantity,
      },
    };
    const res = await extensions.cart.updateItem(payload);
    handleMaxLineitemQuantityError(res);
    if (res?.isError) {
      return res as unknown as Cart;
    }
    mutate(`${USECART_END_POINTS?.getCart}`, res);
  }, []);

  const updateCart = useCallback(async (payload: CartDetails): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.updateCart(payload);
    if (res?.isError) {
      return res as unknown as Cart;
    }
    mutate(`${USECART_END_POINTS?.getCart}`, res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setShippingMethod = useCallback(async (shippingMethodId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      shippingMethod: {
        id: shippingMethodId,
      },
    };

    const res = await extensions.cart.setShippingMethod(payload);

    mutate(`${USECART_END_POINTS?.getCart}`, res);
  }, []);

  const redeemDiscountCode = useCallback(async (code: string) => {
    const extensions = sdk.composableCommerce;
    const payload = { code };
    const res: any = await extensions.cart.redeemDiscountCode(payload);
    const structuredError = safeParseJSON(res?.data?.body);

    if (structuredError?.error) {
      return { isError: true, message: structuredError?.message };
    }

    if ((res.data as Cart)?.cartId) {
      mutate(`${USECART_END_POINTS?.getCart}`, res);
    }

    return res;
  }, []);

  const removeDiscountCode = useCallback(async (discount: Discount) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });

    mutate(`${USECART_END_POINTS?.getCart}`, res);
  }, []);

  const payCashOnDelivery = useCallback(async () => {
    const res = await sdk.callAction({ actionName: `${USECART_END_POINTS?.selectCashOnDelivery}` });
    mutate(`${USECART_END_POINTS?.getCart}`);

    return (res.isError ? {} : res.data) as { success: boolean; message: string; cart?: Cart };
  }, []);

  const resetCashOnDelivery = useCallback(async () => {
    const res = await sdk.callAction({ actionName: `${USECART_END_POINTS?.resetCashOnDelivery}` });
    mutate(`${USECART_END_POINTS?.getCart}`);

    return (res.isError ? {} : res.data) as { success: boolean; message: string; cart?: Cart };
  }, []);

  const payVorkasse = useCallback(async () => {
    const res = await sdk.callAction({ actionName: `${USECART_END_POINTS?.selectVorkassePayment}` });
    mutate(`${USECART_END_POINTS?.getCart}`);

    return (res.isError ? {} : res.data) as { success: boolean; message: string; cart?: Cart };
  }, []);

  const setShippingAddress = useCallback(async (address: Address, isPaypalExpressCheckout?: boolean) => {
    const res = await sdk.callAction({
      actionName: `${USECART_END_POINTS?.setShippingAddress}`,
      payload: { address: address, isPaypalExpressCheckout },
    });
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems,
    transaction,
    addItem,
    updateCart,
    setShippingMethod,
    removeItem,
    updateItem,
    shippingMethods,
    orderCart,
    getOrder,
    orderHistory,
    getProjectSettings,
    redeemDiscountCode,
    removeDiscountCode,
    validateCart,
    addBonusItem,
    payCashOnDelivery,
    resetCashOnDelivery,
    payVorkasse,
    setShippingAddress,
    isUpdateCheckoutSession,
    setIsUpdateCheckoutSession,
  };
};

export default useCart;
